import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import type { Swiper as SwiperClass } from "swiper";

import { RoomMovie } from "../../../api/room";
import { formatLikeCount, movieTitleHandler } from "../../../utils/helpers";
import { genresData } from "../../../constants/structure";
import { ROUTES } from "../../../constants/routes";
import { setLastVisitedId } from "../../../store/slices/currentItem.slice";
import RoomVideoPlayer from "./RoomVideoPlayer";
import MobileProgressController from "../../foryou/_components/MobileComponents/MobileProgressController";
import LikeIcon from "../../../components/icons/room/LikeIcon";
import DislikeIcon from "../../../components/icons/room/DislikeIcon";
import ChatLikeIcon from "../../../components/icons/chat/likeIcon";
import BookMarkIcon from "../../../components/icons/chat/bookMarkIcon";
import Comments from "../../../components/icons/reactionMenu/Comments";
import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";

import styles from "./RoomBrowseMobile.module.scss";
import playerStyles from "./RoomVideoPlayer.module.scss";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

interface RoomBrowseMobileProps {
  movies: RoomMovie[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  muted: boolean;
  onToggleMuted: () => void;
  onLike: () => void;
  onDislike: () => void;
  isSwiping: boolean;
  isCurrentMovieSwiped: boolean;
  onBrokenTrailer: () => void;
  favouritesHandler: (e: any, item: any, isFav: boolean) => void;
  isFavorited: boolean;
  openLoginModal: () => void;
  likesList: number[];
  likeHandler: (e: any, item: any, liked: boolean) => void;
  updateVoteLocal: (id: number, key: "likes_count" | "favorites_count", type: "increment" | "decrement") => void;
  matchesCount: number;
  onToggleSidebar: () => void;
}

const RoomBrowseMobile: React.FC<RoomBrowseMobileProps> = ({
  movies,
  currentIndex,
  onIndexChange,
  muted,
  onToggleMuted,
  onLike,
  onDislike,
  isSwiping,
  isCurrentMovieSwiped,
  onBrokenTrailer,
  favouritesHandler,
  isFavorited,
  openLoginModal,
  likesList,
  likeHandler,
  updateVoteLocal,
  matchesCount,
  onToggleSidebar,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const swiperRef = useRef<SwiperClass | null>(null);
  const seekRef = useRef<((seconds: number) => void) | null>(null);
  const [time, setTime] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  const timelinePlayer = useMemo(
    () => ({ seekTo: (seconds: number) => seekRef.current?.(seconds) }),
    [],
  );

  useEffect(() => {
    const swiper = swiperRef.current;
    if (swiper && swiper.activeIndex !== currentIndex) {
      swiper.slideTo(currentIndex);
    }
  }, [currentIndex]);

  useEffect(() => {
    setTime(0);
    setMaxValue(0);
  }, [currentIndex]);

  const currentMovie = movies[currentIndex] || null;

  const canNavigateUp = currentIndex > 0;
  const canNavigateDown = currentIndex < movies.length - 1;

  const title = currentMovie
    ? movieTitleHandler(i18n.language, currentMovie) || currentMovie.title
    : "";
  const year = currentMovie?.release_date?.slice(0, 4);
  const genreList = currentMovie?.genres
    ? JSON.parse(currentMovie.genres)
      .slice(0, 2)
      .map((id: number) => genresData.find((genre) => genre.id === id)?.name)
      .filter(Boolean)
      .map((name: string) => t(name))
    : [];

  if (!currentMovie) return null;

  const imdbRating = currentMovie.imdb_rating?.toFixed(1) || currentMovie.vote_average?.toFixed(1);
  const tomatoRating = currentMovie.rotten_tomatoes_rating?.toFixed(0);

  const liked = likesList?.includes(currentMovie.id);
  const commentsCount =
    (currentMovie as any).reviews_count ??
    (currentMovie as any).comments_count ??
    currentMovie.shares_count ??
    0;

  const formatCount = (value?: number) => {
    const count = value || 0;
    if (count >= 1000) return `${Math.round(count / 1000)}K`;
    return count;
  };

  const goToDetails = () => {
    navigate(`${ROUTES.DETAILS}/${currentMovie.id}`);
    dispatch(setLastVisitedId(currentMovie.id as number));
  };

  const handleInfoLike = (e: any) => {
    e.stopPropagation();
    const token = localStorage.getItem("accessToken");
    if (!token) return openLoginModal();
    likeHandler(e, currentMovie, liked);
    updateVoteLocal(currentMovie.id, "likes_count", liked ? "decrement" : "increment");
  };

  const handleInfoSaved = (e: any) => {
    e.stopPropagation();
    const token = localStorage.getItem("accessToken");
    if (!token) return openLoginModal();
    favouritesHandler(e, currentMovie, isFavorited);
    updateVoteLocal(currentMovie.id, "favorites_count", isFavorited ? "decrement" : "increment");
  };

  return (
    <div className={styles.browseMobile}>
      <div className={styles.playerArea}>
        <div className={styles.frames} aria-hidden>
          <div className={`${styles.frameBlock} ${styles.frameFar}`} />
          <div className={`${styles.frameBlock} ${styles.frameNear}`} />
        </div>

        <Swiper
          direction="vertical"
          slidesPerView={1}
          grabCursor
          className={styles.mainSwiper}
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper: SwiperClass) => {
            if (swiper.activeIndex !== currentIndex) {
              onIndexChange(swiper.activeIndex);
            }
          }}
        >
          {movies.map((movie, index) => (
            <SwiperSlide key={movie.id} className={styles.mainSlide}>
              <div className={styles.backdrop}>
                {movie.backdrop_path && (
                  <img src={`${IMG_BASE}${movie.backdrop_path}`} alt="" loading="lazy" />
                )}
              </div>
              {index === currentIndex && (
                <div className={styles.video}>
                  <RoomVideoPlayer
                    trailerKey={movie.trailer_key}
                    backdropPath={movie.backdrop_path}
                    muted={muted}
                    onToggleMuted={onToggleMuted}
                    isActive={true}
                    onTrailerError={onBrokenTrailer}
                    carouselMode
                    reportProgress={setTime}
                    reportDuration={setMaxValue}
                    registerSeek={(fn) => {
                      seekRef.current = fn;
                    }}
                    navigation={{
                      mode: "slides",
                      onNavigateUp: () => canNavigateUp && onIndexChange(currentIndex - 1),
                      onNavigateDown: () => canNavigateDown && onIndexChange(currentIndex + 1),
                      canNavigateUp,
                      canNavigateDown,
                    }}
                  />
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className={`${playerStyles.navButtons} ${styles.voteRow}`}>
        <button
          className={`${playerStyles.navBtn} ${isCurrentMovieSwiped ? playerStyles.navBtnVoted : ""}`}
          onClick={onLike}
          disabled={isSwiping || isCurrentMovieSwiped}
          aria-label="Like"
        >
          <LikeIcon />
        </button>
        <button
          className={`${playerStyles.navBtn} ${isCurrentMovieSwiped ? playerStyles.navBtnVoted : ""}`}
          onClick={onDislike}
          disabled={isSwiping || isCurrentMovieSwiped}
          aria-label="Dislike"
        >
          <DislikeIcon />
        </button>
      </div>

      <div className={styles.movieInfo} onClick={goToDetails}>
        {currentMovie.poster_path ? (
          <div className={styles.infoPoster}>
            <img src={`${IMG_BASE}${currentMovie.poster_path}`} alt={title} />
          </div>
        ) : (
          <div className={styles.infoPosterPlaceholder} />
        )}
        <div className={styles.infoText}>
          <h4 className={styles.infoTitle}>{title}</h4>
          {(year || genreList.length > 0) && (
            <div className={styles.infoMeta}>
              {year && <span className={styles.year}>{year}</span>}
              {genreList.length > 0 && (
                <span className={styles.infoGenre}>{genreList.join(", ")}</span>
              )}
            </div>
          )}
          {(imdbRating || tomatoRating) && (
            <div className={styles.ratings}>
              {imdbRating && (
                <div className={styles.ratingItem}>
                  <OverlayRatingIcon />
                  <p className={styles.rating}>{imdbRating}</p>
                </div>
              )}
              {tomatoRating && (
                <div className={styles.ratingItem}>
                  <OverlayTomatoRatingIcon />
                  <p className={styles.rating}>{tomatoRating}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.actionsBar}>
        <div className={styles.actions}>
          <div className={styles.actionsItem} onClick={handleInfoLike}>
            <div className={`${styles.actionsIcon} ${liked ? styles.actionsIconActive : ""}`}>
              <ChatLikeIcon />
            </div>
            <span className={styles.actionsValue}>
              {formatLikeCount(currentMovie.likes_count, { compact: true })}
            </span>
          </div>

          <div className={styles.actionsItem} onClick={goToDetails}>
            <div className={styles.actionsIcon}>
              <Comments />
            </div>
            <span className={styles.actionsValue}>{formatCount(commentsCount)}</span>
          </div>

          <div className={styles.actionsItem} onClick={handleInfoSaved}>
            <div className={`${styles.actionsIcon} ${isFavorited ? styles.actionsIconActive : ""}`}>
              <BookMarkIcon />
            </div>
            <span className={styles.actionsValue}>
              {formatLikeCount(currentMovie.favorites_count, { compact: true })}
            </span>
          </div>
        </div>

        <button className={styles.searchBtn} onClick={goToDetails}>
          {t("room_search_movie", "Search movie")}
        </button>
      </div>

      <div
        className={`${styles.matchButton} ${matchesCount > 0 ? styles.hasMatches : ""}`}
        onClick={onToggleSidebar}
      >
        {t("room_matches", "Совпадения")}
        <span>{matchesCount}</span>
      </div>

      <div className={styles.timeline}>
        <MobileProgressController
          maxValue={maxValue}
          time={time}
          player={timelinePlayer}
          setTime={setTime}
          isMobile={true}
        />
      </div>
    </div>
  );
};

export default RoomBrowseMobile;
